import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { OrderProvider } from './context/OrderContext';
import { useResourcePreloader } from './hooks/useResourcePreloader';

// Lazy load the main chat container to reduce initial bundle size
const EnhancedChatContainer = lazy(() => import('./components/Chat/EnhancedChatContainer'));

function App() {
    // Preload critical resources
    useResourcePreloader();

    // State for background image loading
    const [backgroundLoaded, setBackgroundLoaded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile and handle background loading
    useEffect(() => {
        const checkMobile = () => window.innerWidth < 768;
        setIsMobile(checkMobile());

        const handleResize = () => setIsMobile(checkMobile());
        window.addEventListener('resize', handleResize);

        // Delay background loading on mobile for better performance
        const delay = isMobile ? 1500 : 500; // 1.5s on mobile, 0.5s on desktop

        const timer = setTimeout(() => {
            const img = new Image();
            img.onload = () => {
                setBackgroundLoaded(true);
            };
            img.onerror = () => {
                // If background fails to load, continue without it
                setBackgroundLoaded(true);
            };
            img.src = '/bg.png';
        }, delay);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', handleResize);
        };
    }, [isMobile]);

    return (
        <OrderProvider>
            <Router>
                <div
                    className={`min-h-screen flex flex-col transition-all duration-1000 ease-out ${backgroundLoaded ? 'bg-opacity-100' : 'bg-opacity-0'
                        }`}
                    style={backgroundLoaded ? {
                        backgroundImage: 'url(/bg.png)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                    } : {
                        backgroundColor: '#f8fafc', // Light background while loading
                    }}
                >
                    {/* Background overlay - fade in with background */}
                    <div className={`fixed inset-0 bg-gradient-to-br from-gab-blue/10 via-white/70 to-gab-red/10 backdrop-blur-sm pointer-events-none transition-opacity duration-1000 ${backgroundLoaded ? 'opacity-100' : 'opacity-0'
                        }`}></div>

                    {/* Main Content - No padding on mobile, container on desktop */}
                    <div className="relative z-10 container mx-auto px-0 sm:px-4 max-w-4xl flex flex-col min-h-screen">
                        <div className="flex-1 min-h-0">
                            <Suspense fallback={
                                <div className="min-h-screen flex flex-col items-center justify-center p-4">
                                    {/* Baker Avatar - Static during initial load - Same size as AnimatedAvatarHeader */}
                                    <div className="relative mb-6">
                                        <div className="w-40 h-40 sm:w-36 sm:h-36 rounded-full bg-gradient-to-br from-gab-blue via-primary-blue to-gab-red p-1.5 shadow-neon-blue animate-pulse-slow">
                                            <div className="w-full h-full rounded-full bg-gradient-to-br from-gab-blue via-primary-blue to-gab-red p-0.5">
                                                <div className="w-full h-full rounded-full overflow-hidden bg-white shadow-inner">
                                                    <img
                                                        src="/baker.png"
                                                        alt="Loading..."
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Loading Text */}
                                    <div className="text-center mb-4">
                                        <h3 className="text-lg font-semibold text-slate-800 mb-2">
                                            <span className="bg-gradient-to-r from-gab-blue via-primary-blue to-gab-red bg-clip-text text-transparent font-display tracking-wider">
                                                INITIALIZING SYSTEM...
                                            </span>
                                        </h3>
                                    </div>

                                    {/* Loading animation with bagels - matching MenuDataLoader */}
                                    <div className="flex items-center justify-center space-x-1">
                                        <span className="text-2xl animate-bounce" style={{ animationDelay: '0s' }}>🥯</span>
                                        <span className="text-2xl animate-bounce" style={{ animationDelay: '0.15s' }}>🥯</span>
                                        <span className="text-2xl animate-bounce" style={{ animationDelay: '0.3s' }}>🥯</span>
                                    </div>
                                </div>
                            }>
                                <Routes>
                                    <Route path="/" element={<EnhancedChatContainer />} />
                                </Routes>
                            </Suspense>
                        </div>
                    </div>
                </div>
            </Router>
        </OrderProvider>
    );
}

export default App;
